import type { Schemas } from '@argzombie/api-client';
import {
  ARMOR_FIELD_DEFAULTS,
  ARMOR_LIMITS,
  ARMOR_MAX_REDUCTION,
  HELD_MODELS,
  ITEM_CATEGORIES,
  ITEM_TAGS,
  MAX_ITEM_VALUE,
  THROWABLE_FIELD_DEFAULTS as TD,
  THROWABLE_KINDS,
  THROWABLE_LIMITS as TL,
  WEAPON_CUES,
  WEAPON_STEALTH_DEFAULTS as WS,
  type ArmorDef,
  type ArmorRepairDef,
  type ItemDef,
  type ThrowableDef,
} from '../../lib/validate';
import {
  CheckboxField,
  Fieldset,
  ItemPicker,
  Field,
  IssueList,
  NumberField,
  SelectField,
  SelectInput,
  StackListEditor,
  StringListEditor,
  TextField,
} from './fields';
import { newArmor, newArmorRepair, newThrowable, newWeapon } from './defaults';
import { useContentRefs } from './refs';
import { placeableDefaults } from '../../lib/s2/defaults';
import {
  HAUL_CLASSES,
  NAV_AREAS,
  PLACEABLE_KINDS,
  TECH_ITEM_CATEGORIES,
  type PlaceableDef,
} from '../../lib/s2/types';
import type { FieldErrors } from '../../lib/validate';
import type { FormProps } from './types';
import {
  ARMOR_WIND_DEFAULTS,
  medDefaults,
  WEAPON_WIND_DEFAULTS,
  type MedDef,
} from '../../lib/wind/defaults';
import { SKILL_IDS_BY_FAMILY } from '../../lib/skills/types';

type Item = ItemDef;
/** ADR 0066: `skillId` names the weapon skill that trains and drives it (typed locally until the client is regenerated). */
type Weapon = Schemas['WeaponDef'] & { skillId?: string; manualAction?: boolean };

export function ItemForm({ value: item, onChange, errors }: FormProps<Item>) {
  const set = <K extends keyof Item>(k: K, v: Item[K]) => onChange({ ...item, [k]: v });
  const w = item.weapon;
  const setW = <K extends keyof Weapon>(k: K, v: Weapon[K]) =>
    onChange({ ...item, weapon: { ...w, [k]: v } });
  const a = item.armor;
  const setA = <K extends keyof ArmorDef>(k: K, v: ArmorDef[K]) =>
    onChange({ ...item, armor: { ...a, [k]: v } });
  const repair = a?.repair;
  const setR = <K extends keyof ArmorRepairDef>(k: K, v: ArmorRepairDef[K]) =>
    setA('repair', { ...repair, [k]: v });
  const med = item.med;
  const setMed = <K extends keyof MedDef>(k: K, v: MedDef[K]) =>
    onChange({ ...item, med: { ...med, [k]: v } });
  const t = item.throwable;
  const setT = <K extends keyof ThrowableDef>(k: K, v: ThrowableDef[K]) =>
    onChange({ ...item, throwable: { ...t, [k]: v } });
  const { baseModules } = useContentRefs();
  const repairLevels =
    baseModules.find((m) => m.id === repair?.requiredModuleId)?.levels?.length ?? 0;
  const tags = item.tags ?? [];
  const knownTags: readonly string[] = ITEM_TAGS;
  const otherTags = tags.filter((t) => !knownTags.includes(t));

  return (
    <>
      <Fieldset legend="Basics">
        <TextField
          label="Id"
          mono
          value={item.id}
          issues={errors.id}
          onChange={(v) => set('id', v)}
        />
        <TextField
          label="Name"
          value={item.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <SelectField
          label="Category"
          value={item.category}
          options={[
            ...ITEM_CATEGORIES,
            ...TECH_ITEM_CATEGORIES.filter(
              (c) => !(ITEM_CATEGORIES as readonly string[]).includes(c),
            ),
          ].map((c) => ({ value: c, label: c }))}
          issues={errors.category}
          onChange={(v) => set('category', v ?? '')}
        />
        <NumberField
          label="Tier"
          hint="Tech tier (0 = none)."
          integer
          min={0}
          value={item.tier ?? 0}
          issues={errors.tier}
          onChange={(v) => set('tier', v ?? 0)}
        />
        <NumberField
          label="Weight (kg)"
          min={0}
          step={0.05}
          value={item.weightKg}
          issues={errors.weightKg}
          onChange={(v) => set('weightKg', v ?? 0)}
        />
        <NumberField
          label="Stack size"
          hint="1 means not stackable."
          integer
          min={1}
          value={item.stackSize}
          issues={errors.stackSize}
          onChange={(v) => set('stackSize', v ?? 1)}
        />
        <NumberField
          label="Noise radius (m)"
          hint="When the item is used (weapons, tools). Empty = silent."
          nullable
          min={0}
          value={item.noiseRadiusM}
          issues={errors.noiseRadiusM}
          onChange={(v) => set('noiseRadiusM', v)}
        />
        <NumberField
          label="Heal (HP, old)"
          hint="Old, kept one content version (WIND): the Med block below replaces it. Health restored when used."
          integer
          min={0}
          value={item.heal}
          issues={errors.heal}
          onChange={(v) => set('heal', v ?? 0)}
        />
        <NumberField
          label="Energy"
          hint="Team energy restored when used (consumables, e.g. the energy drink). 0 for none."
          integer
          min={0}
          value={item.energy}
          issues={errors.energy}
          onChange={(v) => set('energy', v ?? 0)}
        />
        <NumberField
          label="Carry bonus (kg)"
          hint="Extra carry capacity when equipped (backpacks). Empty for everything else."
          nullable
          min={0}
          value={item.carryBonusKg}
          issues={errors.carryBonusKg}
          onChange={(v) => set('carryBonusKg', v)}
        />
        <NumberField
          label="Value"
          hint={`Trade/scrap value per unit, 1–${MAX_ITEM_VALUE}; auto-loot ranks by value per kg. 0 = category default.`}
          integer
          min={0}
          max={MAX_ITEM_VALUE}
          value={item.value ?? 0}
          issues={errors.value}
          onChange={(v) => set('value', v ?? 0)}
        />
      </Fieldset>

      <Fieldset legend="Tags">
        {ITEM_TAGS.map((t) => (
          <CheckboxField
            key={t}
            label={t}
            hint={
              t === 'pry'
                ? 'Faster search, forces locked doors quickly.'
                : t === 'lockpick'
                  ? 'Opens locked doors silently.'
                  : 'Risky food or water: whoever eats or drinks it at upkeep may get sick (conditions.riskySickChance).'
            }
            checked={tags.includes(t)}
            onChange={(on) => set('tags', on ? [...tags, t] : tags.filter((x) => x !== t))}
          />
        ))}
        <StringListEditor
          label="Other tags"
          value={otherTags}
          errors={errors}
          base="tags"
          onChange={(next) => set('tags', [...tags.filter((t) => knownTags.includes(t)), ...next])}
        />
      </Fieldset>

      <Fieldset legend="Weapon">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item is a weapon"
            checked={!!w}
            onChange={(on) => set('weapon', on ? newWeapon() : null)}
          />
          <IssueList issues={errors.weapon} />
        </div>
        {w && (
          <>
            <NumberField
              label="Damage"
              hint="Per hit (per pellet for shotguns)."
              integer
              min={0}
              value={w.damage}
              issues={errors['weapon.damage']}
              onChange={(v) => setW('damage', v ?? 0)}
            />
            <SelectField
              label="Weapon skill"
              hint="ADR 0066: the weapon skill this trains and that drives its handling (required)."
              value={(w as Weapon).skillId}
              options={SKILL_IDS_BY_FAMILY.weapon.map((x) => ({ value: x, label: x }))}
              issues={errors['weapon.skillId']}
              onChange={(v) => setW('skillId', v ?? '')}
            />
            {((w.magazineSize ?? 0) > 0 || w.ammoItemId != null) && (
              <div className="sm:col-span-2">
                <CheckboxField
                  label="Manual action"
                  hint="Bolt, pump, draw or cock: the fire interval is x combat.cycle (Rifles, Shotguns, Bows train it). Ranged only."
                  checked={!!(w as Weapon).manualAction}
                  onChange={(on) => setW('manualAction', on)}
                />
                <IssueList issues={errors['weapon.manualAction']} />
              </div>
            )}
            <NumberField
              label="Range (m)"
              min={0}
              value={w.rangeM}
              issues={errors['weapon.rangeM']}
              onChange={(v) => setW('rangeM', v ?? 0)}
            />
            <NumberField
              label="Fire interval (s)"
              min={0}
              step={0.05}
              value={w.fireIntervalS}
              issues={errors['weapon.fireIntervalS']}
              onChange={(v) => setW('fireIntervalS', v ?? 0)}
            />
            <Field label="Ammo" issues={errors['weapon.ammoItemId']} hint="Empty for melee.">
              {(id, invalid) => (
                <ItemPicker
                  id={id}
                  invalid={invalid}
                  none="(none: melee)"
                  filter={(c) => c === 'ammo'}
                  value={w.ammoItemId}
                  onChange={(v) => setW('ammoItemId', v)}
                />
              )}
            </Field>
            <NumberField
              label="Magazine size"
              hint="0 for melee weapons, which never reload."
              integer
              min={0}
              value={w.magazineSize}
              issues={errors['weapon.magazineSize']}
              onChange={(v) => setW('magazineSize', v ?? 0)}
            />
            <NumberField
              label="Reload (s)"
              min={0}
              step={0.1}
              value={w.reloadS}
              issues={errors['weapon.reloadS']}
              onChange={(v) => setW('reloadS', v ?? 0)}
            />
            <NumberField
              label="Pellets"
              integer
              min={1}
              value={w.pellets}
              issues={errors['weapon.pellets']}
              onChange={(v) => setW('pellets', v ?? 1)}
            />
            <NumberField
              label="Spread (deg)"
              min={0}
              value={w.spreadDeg}
              issues={errors['weapon.spreadDeg']}
              onChange={(v) => setW('spreadDeg', v ?? 0)}
            />
            <NumberField
              label="Melee arc (deg)"
              hint="0 for ranged weapons."
              min={0}
              max={360}
              value={w.meleeArcDeg}
              issues={errors['weapon.meleeArcDeg']}
              onChange={(v) => setW('meleeArcDeg', v ?? 0)}
            />
            <NumberField
              label="Durability (shots)"
              hint="Shots before it breaks. Empty = no wear."
              integer
              nullable
              min={1}
              value={w.durabilityShots}
              issues={errors['weapon.durabilityShots']}
              onChange={(v) => setW('durabilityShots', v)}
            />
            <NumberField
              label="Recover chance"
              hint="0..1, chance a spent projectile can be picked up (arrows)."
              min={0}
              max={1}
              step={0.05}
              value={w.recoverChance}
              issues={errors['weapon.recoverChance']}
              onChange={(v) => setW('recoverChance', v ?? 0)}
            />
            <NumberField
              label="Headshot chance"
              hint="0..1 base chance a hit is a headshot (instant kill); per pellet that hits for shotguns. Raid rules shape and cap it."
              min={0}
              max={1}
              step={0.01}
              value={w.headshotChance}
              issues={errors['weapon.headshotChance']}
              onChange={(v) => setW('headshotChance', v ?? 0)}
            />
            <NumberField
              label="Pierce"
              hint="Extra zombies one projectile passes through (crossbow 1, hunting rifle 2). Walls still stop it. 0 for melee."
              integer
              min={0}
              value={w.pierce ?? 0}
              issues={errors['weapon.pierce']}
              onChange={(v) => setW('pierce', v ?? 0)}
            />
            <NumberField
              label="Pierce falloff"
              hint="0..1, damage multiplier per extra zombie (0.7: 110 → 77 → 54)."
              min={0}
              max={1}
              step={0.05}
              value={w.pierceFalloff ?? 0.7}
              issues={errors['weapon.pierceFalloff']}
              onChange={(v) => setW('pierceFalloff', v ?? 0)}
            />
            <NumberField
              label="Knockback (m)"
              hint="How far a hit pushes a zombie, before its knockback resist. Default 0.25."
              min={0}
              step={0.05}
              value={w.knockbackM ?? 0.25}
              issues={errors['weapon.knockbackM']}
              onChange={(v) => setW('knockbackM', v ?? 0)}
            />
            <Field
              label="Held model"
              issues={errors['weapon.heldModel']}
              hint="The mesh in the survivor's hand. Auto = by shape (melee, long gun, pistol)."
            >
              {(id, invalid) => (
                <SelectInput
                  id={id}
                  invalid={invalid}
                  none="(auto: by shape)"
                  value={w.heldModel}
                  options={HELD_MODELS.map((m) => ({ value: m, label: m }))}
                  onChange={(v) => setW('heldModel', v)}
                />
              )}
            </Field>
            <TextField
              label="Attack label"
              hint="The word on the AIM-mode attack button (up to 8 letters, e.g. STAB). Empty: FIRE for ranged, SWING for melee."
              value={(w as { attackLabel?: string | null }).attackLabel ?? ''}
              issues={errors['weapon.attackLabel']}
              onChange={(v) =>
                onChange({
                  ...item,
                  weapon: { ...w, attackLabel: v.trim() === '' ? null : v } as Weapon,
                })
              }
            />
            <Field
              label="Sound cue"
              issues={errors['weapon.audio.cue']}
              hint="The weapon's own sound (audio.cue, usually its item id): the clips in game Resources/Audio/Weapon/<cue>/. None, or a cue with no clips yet, plays the class sound. Melee: the hit (a miss keeps the swing)."
            >
              {(id, invalid) => (
                <SelectInput
                  id={id}
                  invalid={invalid}
                  none="(class sound)"
                  value={w.audio?.cue ?? null}
                  options={WEAPON_CUES.map((c) => ({ value: c, label: c }))}
                  onChange={(v) =>
                    onChange({
                      ...item,
                      weapon: { ...w, audio: v ? { cue: v } : null } as Weapon,
                    })
                  }
                />
              )}
            </Field>
            <CheckboxField
              label="Stealth kill"
              hint="Silent weapon: auto stealth kill on unaware zombies reached from outside their cone."
              checked={w.stealthKill ?? WS.stealthKill}
              onChange={(on) => setW('stealthKill', on)}
            />
            <NumberField
              label="Stealth kill time (s)"
              hint="Seconds a stealth kill with it takes (knife 0.4, crowbar 0.5, bow 0.55). Above 0 on a silent weapon."
              min={0}
              step={0.05}
              value={w.stealthKillS ?? WS.stealthKillS}
              issues={errors['weapon.stealthKillS']}
              onChange={(v) => setW('stealthKillS', v ?? 0)}
            />
            <NumberField
              label="Stealth kill noise (m)"
              hint="Noise of a stealth kill with it (suppressed pistol 2). 0 = the raid rule's killNoiseM."
              min={0}
              step={0.5}
              value={w.stealthKillNoiseM ?? WS.stealthKillNoiseM}
              issues={errors['weapon.stealthKillNoiseM']}
              onChange={(v) => setW('stealthKillNoiseM', v ?? 0)}
            />
            <NumberField
              label="Bloom per shot (deg)"
              hint="0 = no bloom."
              min={0}
              value={w.bloomPerShotDeg ?? 0}
              issues={errors['weapon.bloomPerShotDeg']}
              onChange={(v) => setW('bloomPerShotDeg', v ?? 0)}
            />
            <NumberField
              label="Max bloom (deg)"
              hint="0 = no bloom."
              min={0}
              value={w.maxBloomDeg ?? 0}
              issues={errors['weapon.maxBloomDeg']}
              onChange={(v) => setW('maxBloomDeg', v ?? 0)}
            />
            <NumberField
              label="Fire while moving (multiplier)"
              hint="0 = unchanged."
              min={0}
              value={w.fireMoveMult ?? 0}
              issues={errors['weapon.fireMoveMult']}
              onChange={(v) => setW('fireMoveMult', v ?? 0)}
            />
            <NumberField
              label="Wind cost"
              hint="WIND: wind paid per swing or trigger pull (a shotgun's pellets are one shot). Required on every weapon, 0..50."
              min={0}
              max={50}
              step={0.25}
              value={w.windCost ?? null}
              nullable
              issues={errors['weapon.windCost']}
              onChange={(v) => setW('windCost', v)}
            />
            <NumberField
              label="Wind cost per second"
              hint="WIND: charged while firing (automatics). 0..20, default 0."
              min={0}
              max={20}
              step={0.25}
              value={w.windCostPerS ?? WEAPON_WIND_DEFAULTS.windCostPerS}
              issues={errors['weapon.windCostPerS']}
              onChange={(v) => setW('windCostPerS', v ?? 0)}
            />
            <NumberField
              label="Recovery in hand (multiplier)"
              hint="WIND: wind recovery while this weapon is in hand. 0.3..1.5, default 1."
              min={0.3}
              max={1.5}
              step={0.05}
              value={w.recoveryMult ?? WEAPON_WIND_DEFAULTS.recoveryMult}
              issues={errors['weapon.recoveryMult']}
              onChange={(v) => setW('recoveryMult', v ?? 1)}
            />
          </>
        )}
      </Fieldset>

      <Fieldset legend="Med (WIND)">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item is a med"
            checked={!!med}
            onChange={(on) => set('med', on ? medDefaults() : null)}
            hint="What it does in a raid (wind, wound penalty, tier climb, recovery boost) and at home (rest debt). Replaces Heal."
          />
          <IssueList issues={errors.med} />
        </div>
        {med && (
          <>
            <NumberField
              label="Wind restored"
              hint="In a raid (x field.bandageHeal). 0..200."
              min={0}
              max={200}
              value={med.wind ?? 0}
              issues={errors['med.wind']}
              onChange={(v) => setMed('wind', v ?? 0)}
            />
            <NumberField
              label="Wound penalty cleared"
              hint="Wound capacity steps removed for the raid (the wounds still count toward death). 0..3."
              integer
              min={0}
              max={3}
              value={med.woundPenaltyClear ?? 0}
              issues={errors['med.woundPenaltyClear']}
              onChange={(v) => setMed('woundPenaltyClear', v ?? 0)}
            />
            <NumberField
              label="Tiers climbed"
              hint="Tiers climbed at once in a raid. 0..2."
              integer
              min={0}
              max={2}
              value={med.climbTiers ?? 0}
              issues={errors['med.climbTiers']}
              onChange={(v) => setMed('climbTiers', v ?? 0)}
            />
            <NumberField
              label="Bar after a climb"
              hint="Share of the new tank. 0..1."
              min={0}
              max={1}
              step={0.05}
              value={med.climbBarFrac ?? 0}
              issues={errors['med.climbBarFrac']}
              onChange={(v) => setMed('climbBarFrac', v ?? 0)}
            />
            <NumberField
              label="Recovery boost (multiplier)"
              hint="In-raid recovery boost. 1..3."
              min={1}
              max={3}
              step={0.05}
              value={med.recoveryMult ?? 1}
              issues={errors['med.recoveryMult']}
              onChange={(v) => setMed('recoveryMult', v ?? 1)}
            />
            <NumberField
              label="Recovery boost (s)"
              hint="How long the boost lasts. 0..600."
              min={0}
              max={600}
              value={med.recoveryS ?? 0}
              issues={errors['med.recoveryS']}
              onChange={(v) => setMed('recoveryS', v ?? 0)}
            />
            <NumberField
              label="Rest debt removed (winds)"
              hint="When used at home. 0..3."
              min={0}
              max={3}
              step={0.25}
              value={med.restWinds ?? 0}
              issues={errors['med.restWinds']}
              onChange={(v) => setMed('restWinds', v ?? 0)}
            />
          </>
        )}
      </Fieldset>

      <Fieldset legend="Placeable">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item can be placed"
            checked={!!item.placeable}
            onChange={(on) => set('placeable', on ? placeableDefaults() : null)}
          />
          <IssueList issues={errors.placeable} />
        </div>
        {item.placeable && (
          <PlaceableFields
            p={{ ...placeableDefaults(item.placeable.kind), ...item.placeable }}
            onChange={(p) => set('placeable', p)}
            errors={errors}
          />
        )}
      </Fieldset>

      <Fieldset legend="Mobility (inventory)">
        <SelectField
          label="Goods or raw materials"
          none="(default for the category)"
          value={item.mobility ?? ''}
          options={[
            { value: 'loadout', label: 'loadout (goods: can go in a loadout)' },
            { value: 'haul', label: 'haul (raw materials: carried home, never in a loadout)' },
          ]}
          hint="Empty = from the category: survival food and water haul, medicine tag loadout, else by category. Key parts and workshop tools can't be loadout."
          issues={errors.mobility}
          onChange={(v) => set('mobility', v ?? '')}
        />
      </Fieldset>

      <Fieldset legend="Haul">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item is hauled (key part)"
            checked={!!item.haul}
            onChange={(on) => set('haul', on ? { class: 'pocket', fragile: false } : null)}
          />
          <IssueList issues={errors.haul} />
        </div>
        {item.haul && (
          <>
            <SelectField
              label="Haul class"
              value={item.haul.class}
              options={HAUL_CLASSES.map((c) => ({ value: c, label: c }))}
              issues={errors['haul.class']}
              onChange={(v) => set('haul', { ...item.haul, class: v ?? 'pocket' })}
            />
            <CheckboxField
              label="Fragile"
              hint="Pocket pieces only."
              checked={!!item.haul.fragile}
              onChange={(v) => set('haul', { ...item.haul, fragile: v })}
            />
          </>
        )}
      </Fieldset>

      <Fieldset legend="Armor">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item is armor"
            checked={!!a}
            onChange={(on) => set('armor', on ? newArmor() : null)}
          />
          <p className="text-xs text-bruise">
            WIND: a bite-block downgrades a bite to a hit; a hit loses the reduction share, then the
            wind guard takes it before the tank.
          </p>
          <IssueList issues={errors.armor} />
        </div>
        {a && (
          <>
            <NumberField
              label="Wind guard"
              hint="WIND, replaces bonus health (same numbers): wind a hit loses to the guard first, refilled every raid. 0..200. Empty = not set (reads the old bonus health)."
              integer
              nullable
              min={0}
              max={200}
              placeholder={`not set: bonus health ${a.bonusHealth ?? 0}`}
              value={a.windGuard ?? null}
              issues={errors['armor.windGuard']}
              onChange={(v) => setA('windGuard', v)}
            />
            <NumberField
              label="Wind cost multiplier"
              hint="WIND: folded into melee, bow and push-off costs while worn. 0.5..2, default 1."
              min={0.5}
              max={2}
              step={0.05}
              value={a.windCostMult ?? ARMOR_WIND_DEFAULTS.windCostMult}
              issues={errors['armor.windCostMult']}
              onChange={(v) => setA('windCostMult', v ?? 1)}
            />
            <NumberField
              label="Bite odds multiplier"
              hint="WIND: everyday bite insurance, per piece. 0.2..1, default 1."
              min={0.2}
              max={1}
              step={0.05}
              value={a.biteOddsMult ?? ARMOR_WIND_DEFAULTS.biteOddsMult}
              issues={errors['armor.biteOddsMult']}
              onChange={(v) => setA('biteOddsMult', v ?? 1)}
            />
            <NumberField
              label="Padding (bonus health, old)"
              hint={`Old, kept one content version: Wind guard replaces it. 0..${ARMOR_LIMITS.maxBonusHealth}.`}
              integer
              min={0}
              max={ARMOR_LIMITS.maxBonusHealth}
              value={a.bonusHealth ?? 0}
              issues={errors['armor.bonusHealth']}
              onChange={(v) => setA('bonusHealth', v ?? 0)}
            />
            <NumberField
              label="Blocks per raid"
              hint="Bite-blocks: each downgrades one bite to a hit, refilled every raid. 1..3 (WIND)."
              integer
              min={1}
              max={3}
              value={a.blocksPerRaid ?? 0}
              issues={errors['armor.blocksPerRaid']}
              onChange={(v) => setA('blocksPerRaid', v ?? 0)}
            />
            <NumberField
              label="Block guard (s)"
              hint={`After a block, further attacks are cancelled for free for this long. 0..${ARMOR_LIMITS.maxBlockGuardS}, default ${ARMOR_FIELD_DEFAULTS.blockGuardS}.`}
              min={0}
              max={ARMOR_LIMITS.maxBlockGuardS}
              step={0.05}
              value={a.blockGuardS ?? ARMOR_FIELD_DEFAULTS.blockGuardS}
              issues={errors['armor.blockGuardS']}
              onChange={(v) => setA('blockGuardS', v ?? 0)}
            />
            <NumberField
              label="Durability"
              hint="Total damage prevented before it breaks (lifetime wear, kept between raids)."
              integer
              min={1}
              value={a.durability}
              issues={errors['armor.durability']}
              onChange={(v) => setA('durability', v ?? 0)}
            />
            <NumberField
              label="Move speed multiplier"
              hint={`While worn (heavy armor is slower). ${ARMOR_LIMITS.minMoveSpeedMult}..1, default 1.`}
              min={ARMOR_LIMITS.minMoveSpeedMult}
              max={1}
              step={0.05}
              value={a.moveSpeedMult ?? ARMOR_FIELD_DEFAULTS.moveSpeedMult}
              issues={errors['armor.moveSpeedMult']}
              onChange={(v) => setA('moveSpeedMult', v ?? 0)}
            />
            <NumberField
              label="Movement noise (+m)"
              hint={`Meters added to footstep noise while worn (heavy armor is louder). 0..${ARMOR_LIMITS.maxMovementNoiseAddM}.`}
              min={0}
              max={ARMOR_LIMITS.maxMovementNoiseAddM}
              step={0.1}
              value={a.movementNoiseAddM ?? 0}
              issues={errors['armor.movementNoiseAddM']}
              onChange={(v) => setA('movementNoiseAddM', v ?? 0)}
            />
            <NumberField
              label="Reduction"
              hint={`Advanced: share of each hit's damage blocked, 0..${ARMOR_MAX_REDUCTION}. Kept for later hard-plate tiers; leave 0.`}
              min={0}
              max={ARMOR_MAX_REDUCTION}
              step={0.05}
              value={a.reduction}
              issues={errors['armor.reduction']}
              onChange={(v) => setA('reduction', v ?? 0)}
            />
            <div className="sm:col-span-2">
              <CheckboxField
                label="Repairable at the homebase"
                checked={!!repair}
                onChange={(on) => setA('repair', on ? newArmorRepair() : null)}
              />
            </div>
            {repair && (
              <>
                <NumberField
                  label="Repair restores"
                  hint="Wear removed by one repair (at least 1)."
                  integer
                  min={1}
                  value={repair.restores}
                  issues={errors['armor.repair.restores']}
                  onChange={(v) => setR('restores', v ?? 0)}
                />
                <SelectField
                  label="Repair module"
                  none="(none)"
                  value={repair.requiredModuleId}
                  options={baseModules.map((m) => ({
                    value: m.id ?? '',
                    label: `${m.name || m.id} (${m.id})`,
                  }))}
                  issues={errors['armor.repair.requiredModuleId']}
                  onChange={(v) =>
                    setA('repair', {
                      ...repair,
                      requiredModuleId: v,
                      requiredModuleLevel: v ? Math.max(1, repair.requiredModuleLevel ?? 0) : 0,
                    })
                  }
                />
                <NumberField
                  label="Repair module level"
                  hint={
                    repair.requiredModuleId
                      ? `Minimum level, 1..${repairLevels}.`
                      : 'Ignored without a module.'
                  }
                  integer
                  min={repair.requiredModuleId ? 1 : 0}
                  max={repairLevels || undefined}
                  value={repair.requiredModuleLevel}
                  issues={errors['armor.repair.requiredModuleLevel']}
                  onChange={(v) => setR('requiredModuleLevel', v ?? 0)}
                />
                <StackListEditor
                  label="Repair inputs"
                  hint="Taken from base storage for each repair."
                  base="armor.repair.inputs"
                  value={repair.inputs ?? undefined}
                  errors={errors}
                  onChange={(v) => setR('inputs', v)}
                />
              </>
            )}
          </>
        )}
      </Fieldset>

      <Fieldset legend="Throwable">
        <div className="sm:col-span-2">
          <CheckboxField
            label="This item is a throwable"
            checked={!!t}
            onChange={(on) => set('throwable', on ? newThrowable() : null)}
          />
          <p className="text-xs text-bruise">
            Thrown with the THROW button (outside the two-weapon loadout) and used up by the throw.
            A lure pulls zombies with noise, a blast hurts and pushes, a fire burns an area.
          </p>
          <IssueList issues={errors.throwable} />
        </div>
        {t && (
          <>
            <SelectField
              label="Throwable kind"
              value={t.kind ?? TD.kind}
              options={THROWABLE_KINDS.map((k) => ({ value: k, label: k }))}
              issues={errors['throwable.kind']}
              onChange={(v) => setT('kind', v ?? TD.kind)}
            />
            <NumberField
              label="Throw range (m)"
              hint={`Farthest landing spot from the thrower. 1..${TL.maxThrowRangeM}, default ${TD.throwRangeM}.`}
              min={1}
              max={TL.maxThrowRangeM}
              value={t.throwRangeM ?? TD.throwRangeM}
              issues={errors['throwable.throwRangeM']}
              onChange={(v) => setT('throwRangeM', v ?? 0)}
            />
            <NumberField
              label="Fuse (s)"
              hint={`Seconds from the throw to going off. 0 = on impact (a molotov). 0..${TL.maxFuseS}.`}
              min={0}
              max={TL.maxFuseS}
              step={0.1}
              value={t.fuseS ?? 0}
              issues={errors['throwable.fuseS']}
              onChange={(v) => setT('fuseS', v ?? 0)}
            />
            <NumberField
              label="Bounce"
              hint={`Share of its speed a bounce keeps. 0 = stops where it lands. 0..${TL.maxBounce}.`}
              min={0}
              max={TL.maxBounce}
              step={0.05}
              value={t.bounce ?? 0}
              issues={errors['throwable.bounce']}
              onChange={(v) => setT('bounce', v ?? 0)}
            />
            <NumberField
              label="Radius (m)"
              hint={`Blast or fire radius. 0 for a lure. 0..${TL.maxRadiusM}.`}
              min={0}
              max={TL.maxRadiusM}
              step={0.5}
              value={t.radiusM ?? 0}
              issues={errors['throwable.radiusM']}
              onChange={(v) => setT('radiusM', v ?? 0)}
            />
            <NumberField
              label="Blast damage"
              hint={`Damage at the centre (blasts only). 0..${TL.maxDamage}.`}
              integer
              min={0}
              max={TL.maxDamage}
              value={t.damage ?? 0}
              issues={errors['throwable.damage']}
              onChange={(v) => setT('damage', v ?? 0)}
            />
            <NumberField
              label="Falloff min"
              hint={`Damage at the edge as a share of the centre's, linear in between. 0..1, default ${TD.falloffMin}.`}
              min={0}
              max={1}
              step={0.05}
              value={t.falloffMin ?? TD.falloffMin}
              issues={errors['throwable.falloffMin']}
              onChange={(v) => setT('falloffMin', v ?? 0)}
            />
            <NumberField
              label="Blast knockback (m)"
              hint="Push at the centre, falling off like the damage, before a zombie's knockback resist. 0..3."
              min={0}
              max={3}
              step={0.1}
              value={t.knockbackM ?? 0}
              issues={errors['throwable.knockbackM']}
              onChange={(v) => setT('knockbackM', v ?? 0)}
            />
            <NumberField
              label="Throwable noise (m)"
              hint={`How far it's heard when it goes off (a lure: each pulse). 0..${TL.maxNoiseRadiusM}.`}
              min={0}
              max={TL.maxNoiseRadiusM}
              value={t.noiseRadiusM ?? 0}
              issues={errors['throwable.noiseRadiusM']}
              onChange={(v) => setT('noiseRadiusM', v ?? 0)}
            />
            <NumberField
              label="Lure time (s)"
              hint={`How long a lure runs. 0 for the others. 0..${TL.maxLureS}.`}
              min={0}
              max={TL.maxLureS}
              step={0.5}
              value={t.lureS ?? 0}
              issues={errors['throwable.lureS']}
              onChange={(v) => setT('lureS', v ?? 0)}
            />
            <NumberField
              label="Pulse interval (s)"
              hint={`Seconds between a lure's noise pulses. 0 = one pulse. 0..${TL.maxLureS}.`}
              min={0}
              max={TL.maxLureS}
              step={0.25}
              value={t.pulseS ?? 0}
              issues={errors['throwable.pulseS']}
              onChange={(v) => setT('pulseS', v ?? 0)}
            />
            <NumberField
              label="Fire time (s)"
              hint={`How long a fire burns. 0 for the others. 0..${TL.maxFireS}.`}
              min={0}
              max={TL.maxFireS}
              step={0.5}
              value={t.fireS ?? 0}
              issues={errors['throwable.fireS']}
              onChange={(v) => setT('fireS', v ?? 0)}
            />
            <NumberField
              label="Fire damage per second"
              hint={`Burn damage a second to anything in the fire. 0..${TL.maxFireDps}.`}
              integer
              min={0}
              max={TL.maxFireDps}
              value={t.fireDps ?? 0}
              issues={errors['throwable.fireDps']}
              onChange={(v) => setT('fireDps', v ?? 0)}
            />
            <NumberField
              label="Light radius (m)"
              hint={`Area the fire lights in the fog of war. 0 = no light. 0..${TL.maxRadiusM}.`}
              min={0}
              max={TL.maxRadiusM}
              step={0.5}
              value={t.lightRadiusM ?? 0}
              issues={errors['throwable.lightRadiusM']}
              onChange={(v) => setT('lightRadiusM', v ?? 0)}
            />
            <NumberField
              label="Friendly fire multiplier"
              hint={`Share of the damage the squad takes (zombies always take all of it). 0..1, default ${TD.friendlyFireMult}.`}
              min={0}
              max={1}
              step={0.05}
              value={t.friendlyFireMult ?? TD.friendlyFireMult}
              issues={errors['throwable.friendlyFireMult']}
              onChange={(v) => setT('friendlyFireMult', v ?? 0)}
            />
          </>
        )}
      </Fieldset>
    </>
  );
}

type NumSpec = [
  key: keyof PlaceableDef & string,
  label: string,
  o?: { integer?: boolean; hint?: string; step?: number },
];

const MINE_FIELDS: NumSpec[] = [
  ['arcDeg', 'Arc (deg)'],
  ['rangeM', 'Range (m)'],
  ['tripM', 'Trip radius (m)'],
  ['damage', 'Damage', { integer: true }],
  ['falloffMin', 'Falloff minimum', { hint: 'Share of damage left at the edge, 0..1.' }],
  ['knockbackM', 'Knockback (m)'],
  ['backBlastM', 'Back blast (m)'],
  ['armS', 'Arm time (s)'],
  ['friendlyFireMult', 'Friendly fire multiplier'],
];

/** The placeable block's fields: the kind, footprint and facing, then the numbers of the groups the kind uses plus the common ones. */
function PlaceableFields({
  p,
  onChange,
  errors,
}: {
  p: Required<PlaceableDef>;
  onChange: (next: PlaceableDef) => void;
  errors: FieldErrors;
}) {
  const kind = p.kind;
  const set = <K extends keyof PlaceableDef>(k: K, v: PlaceableDef[K]) =>
    onChange({ ...p, [k]: v });
  const num = ([k, label, o]: NumSpec) => (
    <NumberField
      key={k}
      label={label}
      hint={o?.hint}
      integer={o?.integer}
      min={0}
      value={p[k] as number}
      issues={errors[`placeable.${k}`]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  const fp = [p.footprint[0] ?? 1, p.footprint[1] ?? 1];
  return (
    <>
      <SelectField
        label="Placeable kind"
        value={kind}
        options={PLACEABLE_KINDS.map((k) => ({ value: k, label: k }))}
        issues={errors['placeable.kind']}
        onChange={(v) => set('kind', v ?? 'barricade')}
      />
      <CheckboxField label="Facing" checked={p.facing} onChange={(v) => set('facing', v)} />
      <NumberField
        label="Footprint width"
        hint="Cells. Empty footprint = 1 by 1."
        integer
        min={1}
        value={fp[0]}
        issues={errors['placeable.footprint']}
        onChange={(v) => set('footprint', [v ?? 1, fp[1] ?? 1])}
      />
      <NumberField
        label="Footprint height"
        integer
        min={1}
        value={fp[1]}
        issues={errors['placeable.footprint.1']}
        onChange={(v) => set('footprint', [fp[0] ?? 1, v ?? 1])}
      />
      {num(['placeS', 'Place time (s)'])}
      {num(['noiseRadiusM', 'Noise radius (m)'])}
      {kind === 'mine' && MINE_FIELDS.map(num)}
      {kind === 'barricade' && num(['hp', 'Barricade HP', { integer: true }])}
      {kind === 'trap' && (
        <>
          {num(['slowMult', 'Slow multiplier', { hint: '1 = no slow.' }])}
          {num(['dps', 'Damage per second'])}
          {num(['wear', 'Wear (uses)', { integer: true }])}
        </>
      )}
      {kind === 'light' && num(['lightRadiusM', 'Light radius (m)'])}
      <SelectField
        label="Nav area"
        none="(none)"
        value={p.navArea}
        options={NAV_AREAS.map((a) => ({ value: a, label: a }))}
        issues={errors['placeable.navArea']}
        onChange={(v) => set('navArea', v ?? '')}
      />
    </>
  );
}
